/**
 * Locale-aware comparator for display labels.
 *
 * Uses `Intl.Collator` so that ordering respects the user's language
 * (e.g. Japanese kana vs. English A-Z) rather than raw code-unit order.
 * `numeric: true` keeps values like day-of-month in numeric order.
 *
 * @param {string} [locale] BCP 47 tag; defaults to the runtime default locale.
 * @returns {(a: string, b: string) => number}
 */
export function makeLabelComparator(locale) {
	const collator = new Intl.Collator(locale, { sensitivity: 'base', numeric: true });
	return (a, b) => collator.compare(a, b);
}

/**
 * Sort picker options by their display label using locale-aware collation.
 *
 * Returns a new array; the input is not mutated.
 *
 * @param {Array<{ value: string, label: string }>} options
 * @param {string} [locale] BCP 47 tag; defaults to the runtime default locale.
 * @returns {Array<{ value: string, label: string }>}
 */
export function sortDisplayOptions(options, locale) {
	const compare = makeLabelComparator(locale);
	return [...options].sort((a, b) => compare(a.label, b.label));
}

/**
 * Sort string option values using locale-aware collation.
 *
 * Returns a new array; the input is not mutated.
 *
 * @param {string[]} options
 * @param {string} [locale] BCP 47 tag; defaults to the runtime default locale.
 * @returns {string[]}
 */
export function sortDisplayStrings(options, locale) {
	const compare = makeLabelComparator(locale);
	return [...options].sort(compare);
}
